import {
	Form,
	FormItem,
	TextInput,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import {
	AddTilesResponseModel,
	ShortcutUpdateTileModel,
	ShortcutDetailsTileModel,
	ShortcutCreateTileModel,
	createShortcutTile,
	updateShortcutTile
} from "client/Api/Workspace/workspace";
import { EditTileFromData, initialGenericState } from "client/Features/Workspace/LeftSliders/EditTile/EditGenericTile";
import { EditTileHandle } from "client/Features/Workspace/LeftSliders/EditTile/EditTile";
import { EditTileGeneralFields } from "client/Features/Workspace/LeftSliders/EditTile/GeneralFields";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	TileEditMode,
} from "./common";
import {useCreateTile} from "./hooks/useCreateTile";
import {useUpdateTile} from "./hooks/useUpdateTile";

type ShortcutEditFormData = EditTileFromData<ShortcutUpdateTileModel>;

export type EditShortcutTileProps = {
	isLoading: boolean;
	onIsLoadingChange: (value: boolean) => void;
	initialModel: ShortcutDetailsTileModel;
	mode: TileEditMode;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

const nonConfigurableUrlValidation: IValidationConfiguration<ShortcutEditFormData> =
	createValidationConfiguration<ShortcutEditFormData>();

nonConfigurableUrlValidation
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const configurableUrlValidation: IValidationConfiguration<ShortcutEditFormData> =
	createValidationConfiguration<ShortcutEditFormData>();

configurableUrlValidation
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

configurableUrlValidation
	.forScalar("uri")
	.add(isNotNullNorUndefined(), isNotEmpty());

export const EditShortcutTile = forwardRef(
	(props: EditShortcutTileProps, ref: Ref<EditTileHandle>) => {
		const {
			initialModel,
			mode,
			onIsLoadingChange,
			isLoading,
			onAfterSave,
		} = props;

		const [formData, setFormData] = useState<ShortcutEditFormData>({
			...initialGenericState(initialModel),
			destinationWindow: initialModel.destinationWindow,
			uri: initialModel.canBeConfigured ? initialModel.uri : "",
		});

		const validationBuilder: IValidationConfiguration<ShortcutEditFormData> = initialModel.canBeConfigured
			? configurableUrlValidation
			: nonConfigurableUrlValidation;

		const formHandler: IFormHandler<ShortcutEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { createHandler } = useCreateTile<ShortcutCreateTileModel>(
			formHandler,
			createShortcutTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		const { updateHandler } = useUpdateTile<ShortcutUpdateTileModel>(
			formHandler,
			updateShortcutTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		useImperativeHandle(
			ref,
			() => ({ createHandler, updateHandler })
		);

		return (
			<Form
				onSubmit="NONE"
			>
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				{initialModel.canBeConfigured && (
					<FormItem
						label={localize("_Webpage")}
					>
						<TextInput
							{...formHandler.for("uri").attachToTextInput()}
						/>
						<ValidationError
							{...formHandler
								.for("uri")
								.attachToValidationError()}
						/>
					</FormItem>
				)}
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</Form>
		);
	}
);

EditShortcutTile.displayName = "EditShortcutTile";